import React, { useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import useMediaQuery from "@mui/material/useMediaQuery";
import StackedBarChartOutlinedIcon from "@mui/icons-material/StackedBarChartOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors } from "../../../Shared/colors";
import {
  Card,
  CardHeader,
  CHART_PLOT_HEIGHT,
  StatusColors,
  StatusMeta,
} from "./socialLeadsStyles";

/**
 * Enquiry volume over the selected range, split by what happened to each lead.
 *
 * Two questions in one chart: the height of a bar answers "how many enquiries
 * are we getting", and the split inside it answers "are we actually capturing
 * them" — which is the whole reason this screen exists. A plain total bar would
 * only restate the tile above it.
 *
 * Buckets, their granularity and their counts all come from the server, which
 * derives them from the unfiltered window. Recomputing them here from the
 * visible rows would redraw the chart every time a status tab was clicked.
 */

/** Below this a chart is noise — two or three bars are not a trend. */
const MIN_BUCKETS_FOR_CHART = 3;

const SERIES = [
  { key: "inCrm", label: StatusMeta.inCrm.label },
  { key: "notImported", label: StatusMeta.notImported.label },
  { key: "failed", label: StatusMeta.failed.label },
];

/**
 * A bucket as an axis label.
 *
 * Weeks read as a span because a single date on the axis would be taken for a
 * day. Days drop the month once the range is short enough that it never
 * changes mid-axis, which is most of the time.
 */
const bucketLabel = (bucket, granularity) => {
  const start = moment(bucket.key);
  if (granularity === "day") return start.format("D MMM");

  const end = moment(bucket.end);
  return start.isSame(end, "month")
    ? `${start.format("D")}–${end.format("D MMM")}`
    : `${start.format("D MMM")}–${end.format("D MMM")}`;
};

const VolumeTooltip = ({ active, payload, granularity }) => {
  if (!active || !payload?.length) return null;

  const bucket = payload[0].payload;

  return (
    <TooltipBox>
      <p className="head">
        {granularity === "day"
          ? moment(bucket.key).format("ddd D MMM")
          : `${moment(bucket.key).format("D MMM")} – ${moment(bucket.end).format("D MMM")}`}
      </p>
      {bucket.total === 0 ? (
        <p className="empty">No enquiries</p>
      ) : (
        <>
          {SERIES.filter((series) => bucket[series.key] > 0).map((series) => (
            <p className="line" key={series.key}>
              <i style={{ background: StatusColors[series.key] }} />
              {series.label}
              <strong>{bucket[series.key]}</strong>
            </p>
          ))}
          <p className="line total">
            Total<strong>{bucket.total}</strong>
          </p>
        </>
      )}
    </TooltipBox>
  );
};

export const SocialLeadsVolumeChart = ({ volume }) => {
  // A narrow card cannot fit a tick under every bar; recharts would otherwise
  // draw them overlapping rather than thinning them out.
  const isNarrow = useMediaQuery("(max-width: 620px)");
  const granularity = volume?.granularity || "day";
  const buckets = useMemo(
    () =>
      (volume?.buckets || []).map((bucket) => ({
        ...bucket,
        label: bucketLabel(bucket, granularity),
      })),
    [volume, granularity]
  );

  if (!volume) return null;

  const total = buckets.reduce((sum, bucket) => sum + bucket.total, 0);

  if (buckets.length < MIN_BUCKETS_FOR_CHART || total === 0) {
    return (
      <ChartCard>
        <CardHeader>
          <StackedBarChartOutlinedIcon />
          <h2>Enquiries over time</h2>
        </CardHeader>
        <Placeholder>
          {total === 0
            ? "No enquiries were submitted in this date range."
            : "Pick a wider date range to see how volume is trending."}
        </Placeholder>
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <CardHeader>
        <StackedBarChartOutlinedIcon />
        <h2>Enquiries over time</h2>
        <span className="unit">
          {granularity === "day" ? "By day" : "By week"}
        </span>
      </CardHeader>

      <Body>
        <Legend>
          {SERIES.map((series) => (
            <span className="key" key={series.key}>
              <i style={{ background: StatusColors[series.key] }} />
              {series.label}
            </span>
          ))}
        </Legend>

        <Plot>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={buckets}
              margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
              barCategoryGap="22%"
            >
              <CartesianGrid vertical={false} stroke={colors.inputBorderWhite} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: colors.inputBorderWhite }}
                tick={{ fontSize: isNarrow ? 10 : 11, fill: colors.textMed }}
                interval="preserveStartEnd"
                minTickGap={isNarrow ? 18 : 4}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={isNarrow ? 32 : 44}
                tick={{ fontSize: isNarrow ? 10 : 11, fill: colors.textMed }}
              />
              <Tooltip
                cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                content={<VolumeTooltip granularity={granularity} />}
              />
              {SERIES.map((series, index) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  stackId="volume"
                  fill={StatusColors[series.key]}
                  // Only the top segment of each stack is rounded, so the bar
                  // reads as one column rather than three stacked pills.
                  radius={index === SERIES.length - 1 ? [4, 4, 0, 0] : 0}
                  maxBarSize={54}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Plot>
      </Body>
    </ChartCard>
  );
};

/* Fills the grid row so this card and the response chart beside it end at the
   same line, whichever of the two is intrinsically taller. */
const ChartCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  padding: 12px 16px 10px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/* Grows into whatever height the row settles at, but never below the shared
   plot height — the chart is unreadable squashed shorter than this. */
const Plot = styled.div`
  flex: 1;
  min-height: ${CHART_PLOT_HEIGHT}px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-bottom: 10px;

  .key {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    color: ${colors.textMed};
  }

  i {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
    flex-shrink: 0;
  }
`;

const Placeholder = styled.p`
  margin: 0;
  padding: 26px 18px 30px 18px;
  text-align: center;
  font-size: 0.8rem;
  color: ${colors.textMed};
`;

const TooltipBox = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 9px;
  padding: 9px 11px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);

  .head {
    margin: 0 0 6px 0;
    font-size: 0.74rem;
    font-weight: 700;
    color: ${colors.text};
  }

  .empty {
    margin: 0;
    font-size: 0.74rem;
    color: ${colors.textLight};
  }

  .line {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 3px 0;
    font-size: 0.74rem;
    color: ${colors.textMed};
  }

  .line strong {
    margin-left: auto;
    padding-left: 14px;
    font-variant-numeric: tabular-nums;
    color: ${colors.text};
  }

  .line.total {
    margin: 5px 0 0 0;
    padding-top: 5px;
    border-top: 1px solid ${colors.inputBorderWhite};
    font-weight: 600;
  }

  i {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    display: inline-block;
    flex-shrink: 0;
  }
`;
